import React, { useState, useEffect } from 'react';
import { Table, Button, Container, Row, Col, Form, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const OrderReport = () => {
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ total_orders: 0, total_revenue: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    start: '',
    end: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    total: 0
  });


  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        start: filters.start || undefined,
        end: filters.end || undefined,
        page: pagination.page,
        per_page: pagination.perPage
      };
      const API_BASE = process.env.REACT_APP_API_BASE_URL;

      const response = await axios.get(`${API_BASE}/api/reports/orders/combined`, {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.data.success) {
        setOrders(response.data.data.orders);
        setSummary({
          total_orders: response.data.data.pagination.total,
          total_revenue: response.data.data.orders.reduce(
            (sum, order) => sum + (order.grand_total || 0), 0
          )
        });
        setPagination(prev => ({
          ...prev,
          total: response.data.data.pagination.total
        }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [filters, pagination.page, pagination.perPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handlePerPageChange = (e) => {
    setPagination(prev => ({
      ...prev,
      perPage: parseInt(e.target.value, 10),
      page: 1
    }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const exportPDF = () => {
    const input = document.getElementById('report-table');
    html2canvas(input).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      pdf.addImage(imgData, 'PNG', 10, 10, 190, 0);
      pdf.save('order-report.pdf');
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.perPage);

  return (
    <Container className="mt-4">
      <h2 className="mb-4">Order Report</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            name="start"
            value={filters.start}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            name="end"
            value={filters.end}
            onChange={handleFilterChange}
          />
        </Col>
        <Col md={2}>
          <Form.Select
            value={pagination.perPage}
            onChange={handlePerPageChange}
            disabled={loading}
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Button
            variant="primary"
            onClick={fetchOrders}
            disabled={loading}
          >
            {loading ? <Spinner size="sm" /> : 'Apply'}
          </Button>
        </Col>
        <Col md={2}>
          <Button
            variant="success"
            onClick={exportPDF}
            disabled={loading || orders.length === 0}
          >
            Export PDF
          </Button>
        </Col>
      </Row>

      <div className="mb-3">
        <h5>Total Orders: {summary.total_orders}</h5>
        <h5>Total Revenue: ${summary.total_revenue.toFixed(2)}</h5>
      </div>

      <div id="report-table">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Customer</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Payment</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  <Spinner animation="border" />
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map(order => (
                <tr key={order.order_id}>
                  <td>{order.order_id || 'N/A'}</td>
                  <td>{order.customer_name || 'Unknown'}</td>
                  <td>{order.order_date ? new Date(order.order_date).toLocaleDateString() : 'N/A'}</td>
                  <td>${(order.grand_total || 0).toFixed(2)}</td>
                  <td>
                    <span className={`badge ${
                      order.order_status === 'completed' ? 'bg-success' :
                      order.order_status === 'processing' ? 'bg-warning' :
                      'bg-secondary'
                    }`}>
                      {order.order_status || 'unknown'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${
                      order.payment_status === 'paid' ? 'bg-success' :
                      order.payment_status === 'partial' ? 'bg-warning' :
                      'bg-danger'
                    }`}>
                      {order.payment_status || 'unknown'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  No orders found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Button
            variant="outline-primary"
            disabled={pagination.page === 1 || loading}
            onClick={() => handlePageChange(1)}
            className="me-2"
          >
            First
          </Button>
          <Button
            variant="outline-primary"
            disabled={pagination.page === 1 || loading}
            onClick={() => handlePageChange(pagination.page - 1)}
            className="me-2"
          >
            Previous
          </Button>
          <span className="mx-2">
            Page {pagination.page} of {totalPages}
          </span>
          <Button
            variant="outline-primary"
            disabled={pagination.page >= totalPages || loading}
            onClick={() => handlePageChange(pagination.page + 1)}
            className="me-2"
          >
            Next
          </Button>
          <Button
            variant="outline-primary"
            disabled={pagination.page >= totalPages || loading}
            onClick={() => handlePageChange(totalPages)}
          >
            Last
          </Button>
        </div>
      )}
    </Container>
  );
};

export default OrderReport;